import React, {PureComponent} from "react"

import Loading from "@irati/ui/src/Loading"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import {Hr} from "@irati/ui/src/Layout"
import {Row, Col} from "@irati/ui/src/ColumnRow"
import {Deal, PaymentCurrencyType, PaymentProvider, Wallet, WalletEntityType} from "@irati/types"
import basket from "@irati/checkout/utils/basket"
import isZipEnabled from "@irati/deals/helpers/deals/isZipEnabled"
import {PerksPaymentSelectOnChange, PerksPaymentSelectOption} from "@shared/payments/types"
import authentication from "@irati/auth"
import walletService from "@irati/wallet/graph/wallet"

import {BalanceWrapper} from "./styled"
import PayByZip from "./PayByZip"
import PayByCreditCard from "./PayByCreditCard"

interface Props extends InjectIntl {
  deal: Deal,
  onChange: PerksPaymentSelectOnChange
}

interface State {
  payByCard: boolean,
  payByWallet: boolean,
  payByZip: boolean,
  load?: Promise<void>,
  wallet?: Wallet
}

class PerksPaymentSelect extends PureComponent<Props, State> {
  state: State = {
    payByCard: false,
    payByWallet: false,
    payByZip: false
  }

  componentDidMount() {
    const hasCashWallet = authentication.apps("wallet.users")
    if (hasCashWallet) {
      const load = walletService.get(authentication.user().uuid!, PaymentCurrencyType.Gbp, WalletEntityType.User, {
        fetchPolicy: "network-only"
      }).then((wallet) => {
        this.setState({wallet})
      })
      this.setState({load})
    }

    // Default payment method to Adyen (card)
    this.handleCardOrZip(PaymentProvider.Adyen)
  }

  handleCardOrZip = (value: PaymentProvider) => {
    if (value === PaymentProvider.Zip) {
      this.setState({payByZip: true, payByWallet: false, payByCard: false}, () => this.handleChange())
    } else if (value === PaymentProvider.Adyen) {
      this.setState({payByCard: true, payByZip: false}, () => this.handleChange())
    }
  }

  handleUseWallet = (value: boolean) => {
    if (value) {
      // if you select wallet, it can only be partial payment with card payments, not zip
      this.setState({payByCard: true, payByWallet: true, payByZip: false}, () => this.handleChange())
    } else {
      this.setState({payByWallet: false}, () => this.handleChange())
    }
  }

  handleChange = () => {
    const {payByCard, payByZip, payByWallet, wallet} = this.state

    if (payByZip) {
      basket.paymentProvider = PaymentProvider.Zip
    } else {
      basket.paymentProvider = PaymentProvider.Adyen
    }

    const subscription = basket.subscribe((summary) => {
      if (!summary.isLoading) {
        const total = summary.breakdown.total!.amount!
        const options: PerksPaymentSelectOption[] = []
        if (wallet && payByWallet) {
          const walletAmountToBeUsed = Math.min(basket?.breakdown?.total?.amount || 0, wallet.availableBalanceInCents!)
          options.push({type: PaymentProvider.Wallet, amount: walletAmountToBeUsed})
          const leftOver = total - wallet.availableBalanceInCents!
          if (leftOver > 0) {
            options.push({type: PaymentProvider.Adyen, amount: leftOver})
          }
        } else if (payByCard) {
          options.push({type: PaymentProvider.Adyen, amount: total})
        } else if (payByZip) {
          options.push({type: PaymentProvider.Zip, amount: total})
        }
        this.props.onChange(options)
        subscription.unsubscribe()
      }
    })
  }

  render() {
    const {payByCard, load, payByZip, payByWallet, wallet} = this.state
    const walletAmountToBeUsed = payByWallet ? Math.min(basket?.breakdown?.total?.amount || 0, wallet!.availableBalanceInCents!) : 0

    return (
      <Loading load={load}>
        <Row>
          <Col md={12}>
            <BalanceWrapper>
              <PayByCreditCard
                wallet={wallet}
                walletAmountToBeUsed={walletAmountToBeUsed}
                currency={basket.breakdown.total!.currency!}
                isCardSelected={payByCard}
                isWalletSelected={payByWallet}
                onSelectCard={this.handleCardOrZip}
                onSelectWallet={this.handleUseWallet} />
              {isZipEnabled(this.props.deal) && !basket.isCashback && (
                <>
                  <Hr margin={[2, 0]} />
                  <PayByZip isZipSelected={payByZip} onSelectZip={this.handleCardOrZip} />
                </>
              )}
            </BalanceWrapper>
          </Col>
        </Row>
      </Loading>
    )
  }
}

export default injectIntl(PerksPaymentSelect)
